import { useState } from "react";
import { ArrowRight, Copy, MoreVertical, Pencil, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { WidgetCategory } from "@/types/widget";
import { WidgetEditContext } from "./WidgetEditContext";

export function WidgetCard({
  title,
  category: _category,
  index = 0,
  editMode = false,
  className,
  children,
  onRename,
  onDuplicate,
  onRemove,
  moveSections,
  onMoveToSection,
}: {
  title: string;
  category?: WidgetCategory;
  index?: number;
  editMode?: boolean;
  className?: string;
  children: React.ReactNode;
  onRename?: (title: string) => void;
  onDuplicate?: () => void;
  onRemove?: () => void;
  moveSections?: { id: string; name: string }[];
  onMoveToSection?: (sectionId: string) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(title);

  function commitRename() {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== title) onRename?.(trimmed);
    else setRenameValue(title);
    setRenaming(false);
  }

  const hasMoveTargets = moveSections && moveSections.length > 0 && onMoveToSection;
  const hasActions = editMode && (onRename || onDuplicate || onRemove || hasMoveTargets);

  const editRight =
    hasActions && !renaming ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="p-0.5 rounded text-secondary hover:text-primary hover:bg-surface-elevated transition-colors cursor-pointer"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            aria-label="Widget options"
          >
            <MoreVertical className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-35">
          {onRename && (
            <DropdownMenuItem
              onClick={() => {
                setRenameValue(title);
                setRenaming(true);
              }}
            >
              <Pencil className="h-3.5 w-3.5" />
              Rename
            </DropdownMenuItem>
          )}
          {onDuplicate && (
            <DropdownMenuItem onClick={onDuplicate}>
              <Copy className="h-3.5 w-3.5" />
              Duplicate
            </DropdownMenuItem>
          )}
          {hasMoveTargets && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Move to section</DropdownMenuLabel>
              {moveSections!.map((s) => (
                <DropdownMenuItem key={s.id} onClick={() => onMoveToSection!(s.id)}>
                  <ArrowRight className="h-3.5 w-3.5" />
                  {s.name}
                </DropdownMenuItem>
              ))}
            </>
          )}
          {onRemove && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onRemove}
                className="text-rose-400 focus:text-rose-400 focus:bg-rose-500/10"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Remove
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    ) : null;

  const renameInput = (
    <input
      className="flex-1 bg-transparent text-base font-normal text-primary outline-none border-b border-brand-cyan cursor-text select-text"
      value={renameValue}
      autoFocus
      onChange={(e) => setRenameValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") commitRename();
        if (e.key === "Escape") {
          setRenameValue(title);
          setRenaming(false);
        }
      }}
      onBlur={commitRename}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    />
  );

  return (
    <WidgetEditContext.Provider
      value={{ isEdit: editMode, cardTitle: title, editRight, renaming, renameInput }}
    >
      <div
        className={cn(
          "flex flex-col h-full rounded-lg overflow-hidden bg-surface border animate-widget-in",
          editMode ? "border-brand-cyan/40" : "border-border",
          "shadow-sm",
          className,
        )}
        style={index !== undefined ? { animationDelay: `${index * 0.03}s` } : undefined}
      >
        {children}
      </div>
    </WidgetEditContext.Provider>
  );
}
